/**
 * ledger — Sao lưu / khôi phục / xoá dữ liệu người dùng (GĐ6, không dùng Cloud Functions).
 *
 * Hàng rào an toàn:
 * - Chỉ đụng các collection TRONG DANH SÁCH TRẮNG (không bao giờ đường dẫn tuỳ ý từ file người dùng đưa vào)
 * - Khôi phục chỉ TẠO các bản ghi còn THIẾU theo id — không bao giờ ghi đè hay xoá bản ghi đang có
 * - Xoá dữ liệu chỉ chạy khi gọi tường minh; UI buộc tải sao lưu + gõ xác nhận trước
 * Timestamp được mã hoá {__ts: ISO} — cùng quy ước với scripts/backup.mjs.
 */
import { getFirestore, collection, doc, getDocs, writeBatch, Timestamp } from 'firebase/firestore';
import { APP_ID } from '../config/constants';

type Data = Record<string, any>;
export const TOP_COLLECTIONS = [
  'sources', 'incomes', 'expenses', 'debts', 'creditPayments', 'transfers', 'transactions',
  'plannedExpenses', 'plannedIncomes', 'plannedPayments', 'budgets', 'goals',
  'expenseCategories', 'incomeCategories', 'incomeSources', 'settings', 'years',
  'budgetEnvelopes', 'recurringTemplates', 'healthMetrics'
] as const;
export const DEBT_SUBCOLLECTIONS = ['payments', 'repayments'] as const;

export interface BackupFile {
  app: 'fintrack'; version: 1; exportedAt: string; appId: string;
  /** khoá: 'sources' hoặc 'debts/<id>/payments' */
  collections: Record<string, Array<{ id: string; data: Data }>>;
}

const base = (uid: string) => ['artifacts', APP_ID, 'users', uid] as const;
const ID_OK = /^[A-Za-z0-9_\-:.]{1,200}$/;

export const encode = (v: any): any => {
  if (v && typeof v === 'object') {
    if (typeof v.toDate === 'function') return { __ts: v.toDate().toISOString() };
    if (Array.isArray(v)) return v.map(encode);
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, encode(x)]));
  }
  return v;
};
export const decode = (v: any): any => {
  if (v && typeof v === 'object') {
    if (typeof v.__ts === 'string' && Object.keys(v).length === 1) return Timestamp.fromDate(new Date(v.__ts));
    if (Array.isArray(v)) return v.map(decode);
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, decode(x)]));
  }
  return v;
};

/** Đường dẫn collection hợp lệ trong danh sách trắng? (chặn đường dẫn lạ từ file khôi phục) */
export const isAllowedKey = (key: string): boolean => {
  const parts = key.split('/');
  if (parts.length === 1) return (TOP_COLLECTIONS as readonly string[]).includes(parts[0]);
  return parts.length === 3 && parts[0] === 'debts' && ID_OK.test(parts[1]) && (DEBT_SUBCOLLECTIONS as readonly string[]).includes(parts[2]);
};

const readAll = async (uid: string): Promise<BackupFile['collections']> => {
  const db = getFirestore();
  const [a, b, c, d] = base(uid);
  const out: BackupFile['collections'] = {};
  for (const name of TOP_COLLECTIONS) {
    const snap = await getDocs(collection(db, a, b, c, d, name));
    out[name] = snap.docs.map(x => ({ id: x.id, data: encode(x.data()) }));
  }
  for (const debt of out.debts ?? []) {
    for (const sub of DEBT_SUBCOLLECTIONS) {
      const snap = await getDocs(collection(db, a, b, c, d, 'debts', debt.id, sub));
      if (snap.docs.length) out[`debts/${debt.id}/${sub}`] = snap.docs.map(x => ({ id: x.id, data: encode(x.data()) }));
    }
  }
  return out;
};

export const exportAll = async (uid: string): Promise<BackupFile> => ({
  app: 'fintrack', version: 1, exportedAt: new Date().toISOString(), appId: APP_ID, collections: await readAll(uid)
});

/** Kiểm tra cấu trúc file sao lưu; trả lỗi tiếng Việt hoặc null. */
export const validateBackup = (f: any): string | null => {
  if (!f || f.app !== 'fintrack' || f.version !== 1 || typeof f.collections !== 'object') return 'Đây không phải file sao lưu FinTrack hợp lệ.';
  for (const [key, rows] of Object.entries(f.collections as Record<string, any>)) {
    if (!isAllowedKey(key)) return `File chứa mục không được phép: "${key}".`;
    if (!Array.isArray(rows)) return `Mục "${key}" sai định dạng.`;
    for (const r of rows) if (!r || typeof r.id !== 'string' || !ID_OK.test(r.id) || typeof r.data !== 'object') return `Mục "${key}" có bản ghi sai định dạng.`;
  }
  return null;
};

export interface RestorePlan { rows: Array<{ key: string; total: number; missing: number }>; totalMissing: number }

/** So file với dữ liệu hiện có: mỗi mục có bao nhiêu bản ghi CÒN THIẾU (sẽ được tạo). */
export const planRestore = async (uid: string, f: BackupFile): Promise<RestorePlan> => {
  const err = validateBackup(f);
  if (err) throw new Error(err);
  const db = getFirestore();
  const [a, b, c, d] = base(uid);
  const rows: RestorePlan['rows'] = [];
  for (const [key, list] of Object.entries(f.collections)) {
    const have = new Set((await getDocs(collection(db, a, b, c, d, ...key.split('/')))).docs.map(x => x.id));
    rows.push({ key, total: list.length, missing: list.filter(r => !have.has(r.id)).length });
  }
  return { rows, totalMissing: rows.reduce((s, r) => s + r.missing, 0) };
};

export const applyRestore = async (uid: string, f: BackupFile, onProgress?: (done: number, total: number) => void) => {
  const plan = await planRestore(uid, f); // kiểm tra lại ngay trước khi ghi
  const db = getFirestore();
  const [a, b, c, d] = base(uid);
  const failures: Array<{ key: string; error: string }> = [];
  let written = 0, done = 0;
  for (const [key, list] of Object.entries(f.collections)) {
    const have = new Set((await getDocs(collection(db, a, b, c, d, ...key.split('/')))).docs.map(x => x.id));
    const missing = list.filter(r => !have.has(r.id));
    for (let i = 0; i < missing.length; i += 400) {
      const chunk = missing.slice(i, i + 400);
      try {
        const batch = writeBatch(db);
        for (const r of chunk) batch.set(doc(db, a, b, c, d, ...key.split('/'), r.id), decode(r.data));
        await batch.commit();
        written += chunk.length;
      } catch (e) { failures.push({ key, error: (e as Error).message }); }
      done += chunk.length;
      onProgress?.(done, plan.totalMissing);
    }
  }
  return { written, failures };
};

/** Xoá TOÀN BỘ dữ liệu của người dùng (không xoá tài khoản đăng nhập). Gọi sau khi UI đã bắt tải sao lưu + gõ xác nhận. */
export const deleteAllData = async (uid: string, onProgress?: (done: number) => void): Promise<{ deleted: number; failures: string[] }> => {
  const db = getFirestore();
  const [a, b, c, d] = base(uid);
  const targets: Array<{ key: string; ids: string[] }> = [];
  const debts = (await getDocs(collection(db, a, b, c, d, 'debts'))).docs.map(x => x.id);
  for (const debt of debts) for (const sub of DEBT_SUBCOLLECTIONS) {
    targets.push({ key: `debts/${debt}/${sub}`, ids: (await getDocs(collection(db, a, b, c, d, 'debts', debt, sub))).docs.map(x => x.id) });
  }
  for (const name of TOP_COLLECTIONS) targets.push({ key: name, ids: (await getDocs(collection(db, a, b, c, d, name))).docs.map(x => x.id) });

  let deleted = 0; const failures: string[] = [];
  for (const t of targets) {
    for (let i = 0; i < t.ids.length; i += 400) {
      const chunk = t.ids.slice(i, i + 400);
      try {
        const batch = writeBatch(db);
        for (const id of chunk) batch.delete(doc(db, a, b, c, d, ...t.key.split('/'), id));
        await batch.commit();
        deleted += chunk.length; onProgress?.(deleted);
      } catch (e) { failures.push(`${t.key}: ${(e as Error).message}`); }
    }
  }
  return { deleted, failures };
};
